import { useEffect, useState } from 'react';
import { useSimulationStore } from '../../store/simulationStore';
import {
  DURATION_PRESETS_MINUTES,
  formatDurationMinutes,
  MAX_VISUAL_FPS,
  MIN_VISUAL_FPS,
  VISUAL_FPS_PRESETS,
  formatVisualFpsLabel,
  MAX_DURATION_MINUTES,
  type SimulationDurationMode,
  type SimulationRunMode,
} from '../../lib/simulationDuration';

interface RunSimulationDialogProps {
  open: boolean;
  projectId: string;
  onClose: () => void;
  onRun: () => void;
}

export default function RunSimulationDialog({
  open,
  projectId,
  onClose,
  onRun,
}: RunSimulationDialogProps) {
  const storedMode = useSimulationStore((s) => s.simulationDurationMode);
  const storedRunMode = useSimulationStore((s) => s.simulationRunMode);
  const storedMinutes = useSimulationStore((s) => s.durationMinutes);
  const storedFps = useSimulationStore((s) => s.visualFps);
  const setSimulationDurationMode = useSimulationStore(
    (s) => s.setSimulationDurationMode,
  );
  const setSimulationRunMode = useSimulationStore((s) => s.setSimulationRunMode);
  const setDurationMinutes = useSimulationStore((s) => s.setDurationMinutes);
  const setVisualFps = useSimulationStore((s) => s.setVisualFps);

  const [mode, setMode] = useState<SimulationDurationMode>(storedMode);
  const [runMode, setRunMode] = useState<SimulationRunMode>(storedRunMode);
  const [durationMinutes, setDurationMinutesLocal] = useState(storedMinutes);
  const [visualFps, setVisualFpsLocal] = useState(storedFps);

  useEffect(() => {
    if (!open) return;
    void useSimulationStore.getState().hydrateRunPreferences(projectId);
  }, [open, projectId]);

  useEffect(() => {
    if (!open) return;
    const lastSubmitted = useSimulationStore.getState().lastSubmittedRunConfig;
    const source = lastSubmitted ?? {
      run_mode: storedRunMode,
      duration_mode: storedMode,
      duration_minutes: storedMinutes,
      visual_fps: storedFps,
      show_products: useSimulationStore.getState().showProductSprites,
      active_scenario_id: useSimulationStore.getState().activeScenarioId,
    };
    setMode(source.duration_mode);
    setRunMode(source.run_mode);
    setDurationMinutesLocal(source.duration_minutes);
    setVisualFpsLocal(source.visual_fps);
  }, [open, storedMode, storedRunMode, storedMinutes, storedFps]);

  if (!open) return null;

  const handleSubmit = () => {
    setSimulationDurationMode(mode);
    setSimulationRunMode(runMode);
    setDurationMinutes(durationMinutes);
    setVisualFps(visualFps);
    onRun();
    onClose();
  };

  const durationInvalid =
    mode === 'fixed' &&
    (durationMinutes < 1 || durationMinutes > MAX_DURATION_MINUTES);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="run-simulation-title"
        className="relative w-full max-w-md rounded-xl border border-border bg-surface-raised shadow-2xl"
      >
        <header className="border-b border-border px-5 py-4">
          <h2 id="run-simulation-title" className="text-lg font-semibold text-text">
            Run simulation
          </h2>
          <p className="mt-1 text-xs text-text-muted">
            Choose how to run: fast calculation or live visualization on the canvas.
          </p>
        </header>

        <div className="space-y-5 px-5 py-4">
          <fieldset>
            <legend className="mb-2 block text-xs font-medium text-text-muted">
              Run mode
            </legend>
            <div className="space-y-2">
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-surface/40 p-3 transition hover:border-accent/40">
                <input
                  type="radio"
                  name="sim-run-mode"
                  className="mt-0.5"
                  checked={runMode === 'calculate'}
                  onChange={() => setRunMode('calculate')}
                />
                <span>
                  <span className="block text-sm font-medium text-text">
                    Calculate only
                  </span>
                  <span className="mt-0.5 block text-xs text-text-subtle">
                    Runs as fast as possible and shows results when finished. The
                    canvas stays editable.
                  </span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-surface/40 p-3 transition hover:border-accent/40">
                <input
                  type="radio"
                  name="sim-run-mode"
                  className="mt-0.5"
                  checked={runMode === 'realtime'}
                  onChange={() => setRunMode('realtime')}
                />
                <span>
                  <span className="block text-sm font-medium text-text">
                    Real-time simulation
                  </span>
                  <span className="mt-0.5 block text-xs text-text-subtle">
                    Shows products moving on routes live. Click gates on the canvas
                    or in the Gates panel to divert flow while running.
                  </span>
                </span>
              </label>
            </div>
          </fieldset>

          <fieldset>
            <legend className="mb-2 block text-xs font-medium text-text-muted">
              Simulation length
            </legend>
            <div className="space-y-2">
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-surface/40 p-3 transition hover:border-accent/40">
                <input
                  type="radio"
                  name="sim-length"
                  className="mt-0.5"
                  checked={mode === 'until_stopped'}
                  onChange={() => setMode('until_stopped')}
                />
                <span>
                  <span className="block text-sm font-medium text-text">
                    Run until I stop
                  </span>
                  <span className="mt-0.5 block text-xs text-text-subtle">
                    Continues indefinitely until you click Stop simulation.
                  </span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-surface/40 p-3 transition hover:border-accent/40">
                <input
                  type="radio"
                  name="sim-length"
                  className="mt-0.5"
                  checked={mode === 'fixed'}
                  onChange={() => setMode('fixed')}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-text">
                    Fixed duration
                  </span>
                  <span className="mt-0.5 block text-xs text-text-subtle">
                    Stops automatically after the chosen time.
                  </span>
                </span>
              </label>
            </div>
          </fieldset>

          {mode === 'fixed' && (
            <div>
              <label className="mb-2 block text-xs font-medium text-text-muted">
                Duration
              </label>
              <div className="mb-2 flex flex-wrap gap-2">
                {DURATION_PRESETS_MINUTES.map((minutes) => (
                  <button
                    key={minutes}
                    type="button"
                    className={`rounded-md border px-2.5 py-1 text-xs transition ${
                      durationMinutes === minutes
                        ? 'border-accent bg-accent/15 text-accent'
                        : 'border-border bg-surface/40 text-text-muted hover:border-accent/50'
                    }`}
                    onClick={() => setDurationMinutesLocal(minutes)}
                  >
                    {formatDurationMinutes(minutes)}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={MAX_DURATION_MINUTES}
                  step={1}
                  className="input-field w-28"
                  value={durationMinutes}
                  onChange={(e) =>
                    setDurationMinutesLocal(Number(e.target.value))
                  }
                />
                <span className="text-xs text-text-muted">minutes (up to 7 days)</span>
              </div>
              {durationInvalid && (
                <p className="mt-1 text-xs text-danger">
                  Enter between 1 and {MAX_DURATION_MINUTES.toLocaleString()} minutes.
                </p>
              )}
            </div>
          )}

          {runMode === 'realtime' && (
            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <label
                  htmlFor="visual-fps"
                  className="text-xs font-medium text-text-muted"
                >
                  Visual frame rate
                </label>
                <span className="text-xs tabular-nums text-text-subtle">
                  {formatVisualFpsLabel(visualFps)}
                </span>
              </div>
              <input
                id="visual-fps"
                type="range"
                min={MIN_VISUAL_FPS}
                max={MAX_VISUAL_FPS}
                step={1}
                className="w-full accent-accent"
                value={visualFps}
                onChange={(e) => setVisualFpsLocal(Number(e.target.value))}
              />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {VISUAL_FPS_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    className={`rounded-md border px-2 py-0.5 text-[11px] transition ${
                      visualFps === preset
                        ? 'border-accent bg-accent/15 text-accent'
                        : 'border-border bg-surface/40 text-text-muted hover:border-accent/50'
                    }`}
                    onClick={() => setVisualFpsLocal(preset)}
                  >
                    {preset} FPS
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-text-subtle">
                Controls visual smoothness, not simulation speed. Higher FPS uses
                more browser, network, and server resources.
              </p>
            </div>
          )}
        </div>

        <footer className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <button type="button" className="btn-ghost px-4 py-2 text-sm" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary px-4 py-2 text-sm"
            disabled={!projectId || durationInvalid}
            onClick={handleSubmit}
          >
            Start simulation
          </button>
        </footer>
      </div>
    </div>
  );
}
